import * as THREE from 'three'

import Camera from './Camera'
import Renderer from './Renderer'
import World from './World/World'
import PlayerController from './player/PlayerController'
import Debug from './utils/Debug'
import Sizes from './utils/Sizes'
import Time from './utils/Time'
import Resources from './utils/Resources'
import { sources } from './sources'

/**
 * Root of the application. Implemented as a singleton so any module can reach
 * the shared state with `new Experience()` without prop-drilling references.
 *
 * The first call (from `main.ts`) must pass the canvas; every later call simply
 * returns the existing instance.
 */
export default class Experience {
  private static _instance: Experience | null = null

  // Definite-assignment (`!`): the singleton guard means the constructor can
  // return early before these run, but they are always set on the real instance.
  public readonly canvas!: HTMLCanvasElement
  public readonly debug!: Debug
  public readonly sizes!: Sizes
  public readonly time!: Time
  public readonly scene!: THREE.Scene
  public readonly resources!: Resources
  public readonly camera!: Camera
  public readonly renderer!: Renderer
  public readonly world!: World
  public readonly player!: PlayerController

  constructor(canvas?: HTMLCanvasElement) {
    if (Experience._instance) {
      return Experience._instance
    }
    Experience._instance = this

    if (!canvas) {
      throw new Error('Experience must be created with a canvas on first use.')
    }

    this.canvas = canvas

    // Order matters: utils first, then scene graph, then renderer, then world.
    this.debug = new Debug()
    this.sizes = new Sizes()
    this.time = new Time()
    this.scene = new THREE.Scene()
    this.resources = new Resources(sources)
    this.camera = new Camera()
    this.renderer = new Renderer()
    this.world = new World()
    this.player = new PlayerController()

    // Wire global events.
    this.sizes.on('resize', () => this.resize())
    this.time.on('tick', () => this.update())
  }

  /** Convenience accessor for the active instance. */
  public static get instance(): Experience {
    if (!Experience._instance) {
      throw new Error('Experience has not been initialised yet.')
    }
    return Experience._instance
  }

  private resize(): void {
    this.camera.resize()
    this.renderer.resize()
    this.player.resize()
  }

  private update(): void {
    this.debug.stats?.begin()

    this.player.update(this.time.delta / 1000)
    this.world.update()
    this.renderer.update()

    this.debug.stats?.end()
  }

  /** Dispose of every GPU resource and detach listeners (used on HMR/unmount). */
  public destroy(): void {
    this.sizes.off('resize')
    this.time.off('tick')
    this.time.stop()

    this.scene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()

        const material = child.material as THREE.Material | THREE.Material[]
        const materials = Array.isArray(material) ? material : [material]
        for (const mat of materials) {
          for (const value of Object.values(mat)) {
            if (value instanceof THREE.Texture) value.dispose()
          }
          mat.dispose()
        }
      }
    })

    this.player.dispose()
    this.renderer.dispose()
    this.debug.destroy()

    Experience._instance = null
  }
}
